/**
 * Swipe a row sideways to act on it (TRUS-759), for touch screens: phones and
 * tablets in a browser, touchscreen laptops, the desktop app on a touchscreen;
 * and with a two-finger trackpad swipe (TRUS-760) on a Mac or precision touchpad,
 * in the browser and the desktop app.
 *
 *  - Finger, pen or trackpad. A mouse drag never swipes: it would fight text
 *    selection and the drag-to-reorder of pinned Team rows, and mouse users
 *    already have the row's buttons and menus.
 *  - The gesture locks to an axis after LOCK_PX. Vertical means scrolling: the
 *    swipe is abandoned and the list scrolls (rows set `touch-action: pan-y`).
 *  - Releasing past COMMIT_RATIO of the row width, or a fast flick, runs the
 *    action for that side; anything less springs back. The row always springs
 *    back - these actions never remove it from under the finger.
 *  - The click that a browser fires after a swipe is swallowed, so swiping a
 *    row does not also open it.
 *
 * Every action a swipe runs must also have a visible button or menu item.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

export type SwipeSide = 'left' | 'right';

/** Pixels of movement before the gesture picks an axis. */
export const LOCK_PX = 10;
/** Fraction of the row width past which a release commits. */
export const COMMIT_RATIO = 0.35;
/** A flick this fast (px/ms) commits even when short... */
export const FLICK_VELOCITY = 0.5;
/** ...as long as it travelled at least this far. */
export const FLICK_MIN_PX = 40;
/** Width to assume when layout gives none (tests, detached nodes). */
const FALLBACK_WIDTH = 320;
/** How long after a swipe the follow-up click is swallowed. */
const CLICK_SUPPRESS_MS = 400;
/** Trackpad: no wheel event for this long ends the swipe (there is no "lift" event). */
export const WHEEL_END_MS = 140;
/** Trackpad: after a swipe ends, wheel events are ignored until they pause this long (momentum tail). */
export const WHEEL_COOLDOWN_MS = 220;

export interface UseSwipeActionsOptions {
  /** Swiping toward the right (finger moves left to right) runs this. */
  onSwipeRight?: () => void;
  /** Swiping toward the left runs this. */
  onSwipeLeft?: () => void;
  disabled?: boolean;
  /** Commit distance in px instead of a fraction of the width (wide rows that move a little, e.g. swipe-to-reply). */
  commitPx?: number;
  /** Fraction of the row width a swipe must travel (TRUS-761 sensitivity). Default COMMIT_RATIO. */
  commitRatio?: number;
  /** Two-finger trackpad swipe on (TRUS-761 setting). Default true. */
  trackpad?: boolean;
  /** Flip the trackpad direction (TRUS-761 setting). Default false. */
  trackpadReverse?: boolean;
}

export interface SwipeState {
  /** Current horizontal offset of the row content, px (positive = right). */
  offset: number;
  /** True while a finger is dragging the row. */
  dragging: boolean;
  /** Which side's action is showing (by drag direction), if any. */
  side: SwipeSide | null;
  /** True once the drag has passed the commit point (for the panel's emphasis). */
  armed: boolean;
}

interface Gesture {
  pointerId: number;
  startX: number;
  startY: number;
  startT: number;
  lastX: number;
  lastT: number;
  mode: 'pending' | 'horizontal' | 'abandoned';
  width: number;
}

/** Event time in ms; same clock for every event of a gesture. */
function timeOf(e: React.PointerEvent): number {
  return typeof e.timeStamp === 'number' && Number.isFinite(e.timeStamp) ? e.timeStamp : Date.now();
}

function isTouchLike(e: React.PointerEvent): boolean {
  return e.pointerType === 'touch' || e.pointerType === 'pen';
}

export function useSwipeActions({
  onSwipeRight,
  onSwipeLeft,
  disabled,
  commitPx,
  commitRatio = COMMIT_RATIO,
  trackpad = true,
  trackpadReverse = false,
}: UseSwipeActionsOptions) {
  const ref = useRef<HTMLDivElement | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const suppressUntil = useRef(0);
  const [state, setState] = useState<SwipeState>({ offset: 0, dragging: false, side: null, armed: false });

  // Latest callbacks without re-creating the handlers on every render.
  const actions = useRef({ onSwipeRight, onSwipeLeft });
  actions.current = { onSwipeRight, onSwipeLeft };
  const commitAt = (width: number) => commitPx ?? width * commitRatio;
  const commitAtRef = useRef(commitAt);
  commitAtRef.current = commitAt;

  const reset = useCallback(() => {
    gesture.current = null;
    setState({ offset: 0, dragging: false, side: null, armed: false });
  }, []);

  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || !isTouchLike(e) || (e.button !== undefined && e.button > 0)) return;
      const width = ref.current?.offsetWidth || FALLBACK_WIDTH;
      gesture.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startT: timeOf(e),
        lastX: e.clientX,
        lastT: timeOf(e),
        mode: 'pending',
        width,
      };
    },
    [disabled],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId || g.mode === 'abandoned') return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (g.mode === 'pending') {
      if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
      const side: SwipeSide = dx > 0 ? 'right' : 'left';
      const hasAction = side === 'right' ? !!actions.current.onSwipeRight : !!actions.current.onSwipeLeft;
      if (Math.abs(dy) >= Math.abs(dx) || !hasAction) {
        g.mode = 'abandoned';
        return;
      }
      g.mode = 'horizontal';
      try {
        (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      } catch {
        /* capture is a nicety; jsdom and some browsers refuse it */
      }
    }
    g.lastX = e.clientX;
    g.lastT = timeOf(e);
    const side: SwipeSide = dx > 0 ? 'right' : 'left';
    const hasAction = side === 'right' ? !!actions.current.onSwipeRight : !!actions.current.onSwipeLeft;
    // No action that way: hold at the edge instead of revealing an empty panel.
    const raw = hasAction ? dx : 0;
    // Past the row width the content resists instead of leaving the screen.
    const limit = g.width * 0.9;
    const offset = Math.abs(raw) > limit ? Math.sign(raw) * (limit + (Math.abs(raw) - limit) * 0.2) : raw;
    setState({
      offset,
      dragging: true,
      side: hasAction ? side : null,
      armed: hasAction && Math.abs(dx) >= commitAtRef.current(g.width),
    });
  }, []);

  const finish = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
      const g = gesture.current;
      if (!g || g.pointerId !== e.pointerId) return;
      if (g.mode === 'horizontal') {
        // A click follows pointerup on the row: it must not open the row too.
        suppressUntil.current = Date.now() + CLICK_SUPPRESS_MS;
        if (!cancelled) {
          const dx = e.clientX - g.startX;
          const now = timeOf(e);
          const dt = Math.max(1, now - g.startT);
          const velocity = Math.abs(dx) / dt;
          const commit = Math.abs(dx) >= commitAtRef.current(g.width) || (velocity >= FLICK_VELOCITY && Math.abs(dx) >= FLICK_MIN_PX);
          if (commit) {
            const run = dx > 0 ? actions.current.onSwipeRight : actions.current.onSwipeLeft;
            run?.();
          }
        }
      }
      reset();
    },
    [reset],
  );

  // Trackpad two-finger swipe (TRUS-760): the web and desktop app on a Mac or a
  // precision touchpad. A horizontal wheel gesture moves the row like a finger;
  // it ends when the wheel events stop, and commits on the same distance. Vertical
  // scrolling and pinch-zoom (ctrlKey) pass through untouched. preventDefault
  // keeps a horizontal swipe from also scrolling sideways or going back a page,
  // so the listener is attached natively as non-passive.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const trackpadRef = useRef(trackpad);
  trackpadRef.current = trackpad;
  const reverseRef = useRef(trackpadReverse);
  reverseRef.current = trackpadReverse;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let dx = 0;
    let dy = 0;
    let active = false;
    // TRUS-761: Chrome lets a page cancel only the FIRST wheel event of a trackpad
    // gesture. Waiting for a few pixels let a slow start go to the browser, which
    // then scrolled or took the swipe as "back" - why right swipes failed. So the
    // first horizontal event claims the gesture ('pending'), and it is handed back
    // as a scroll if it turns out vertical before it travels LOCK_PX sideways.
    let locked = false;
    let width = FALLBACK_WIDTH;
    let endTimer: ReturnType<typeof setTimeout> | null = null;
    let coolTimer: ReturnType<typeof setTimeout> | null = null;
    let cooling = false;

    const end = () => {
      endTimer = null;
      if (!active) return;
      active = false;
      const wasLocked = locked;
      locked = false;
      dy = 0;
      if (!wasLocked) {
        // Never travelled far enough sideways to be a swipe.
        dx = 0;
        setState({ offset: 0, dragging: false, side: null, armed: false });
        return;
      }
      const commit = Math.abs(dx) >= commitAtRef.current(width);
      const run = dx > 0 ? actions.current.onSwipeRight : actions.current.onSwipeLeft;
      dx = 0;
      suppressUntil.current = Date.now() + CLICK_SUPPRESS_MS;
      setState({ offset: 0, dragging: false, side: null, armed: false });
      cooling = true;
      coolTimer = setTimeout(() => (cooling = false), WHEEL_COOLDOWN_MS);
      if (commit) run?.();
    };

    const onWheel = (e: WheelEvent) => {
      if (disabledRef.current || !trackpadRef.current || e.ctrlKey) return;
      if (cooling) {
        // Still the momentum of the swipe that just ended: swallow sideways motion,
        // and wait for a pause before a new swipe may start.
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault();
        if (coolTimer) clearTimeout(coolTimer);
        coolTimer = setTimeout(() => (cooling = false), WHEEL_COOLDOWN_MS);
        return;
      }
      if (!active) {
        if (e.deltaX === 0 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        active = true;
        locked = false;
        dx = 0;
        dy = 0;
        width = el.offsetWidth || FALLBACK_WIDTH;
      }
      // WebKit (Safari, the macOS desktop app) says whether the system scroll
      // direction is "natural"; with it off, fingers moving right give a positive
      // deltaX, so flip it to keep "swipe right" meaning the fingers' direction.
      const inverted = (e as WheelEvent & { webkitDirectionInvertedFromDevice?: boolean }).webkitDirectionInvertedFromDevice;
      // Chrome does not say, so it assumes natural (the macOS default); the user's
      // "Reverse trackpad direction" setting covers the rest.
      const sign = (inverted === false ? 1 : -1) * (reverseRef.current ? -1 : 1);
      dx += sign * e.deltaX;
      dy += Math.abs(e.deltaY);
      if (!locked) {
        if (dy > Math.abs(dx) + 2) {
          // Turned out to be a vertical scroll: hand it back to the browser.
          active = false;
          dx = 0;
          dy = 0;
          if (endTimer) clearTimeout(endTimer);
          endTimer = null;
          return;
        }
        e.preventDefault();
        if (Math.abs(dx) >= LOCK_PX) locked = true;
        if (endTimer) clearTimeout(endTimer);
        endTimer = setTimeout(end, WHEEL_END_MS);
        if (!locked) return;
      }
      e.preventDefault();
      const side: SwipeSide = dx > 0 ? 'right' : 'left';
      const hasAction = side === 'right' ? !!actions.current.onSwipeRight : !!actions.current.onSwipeLeft;
      const raw = hasAction ? dx : 0;
      const limit = width * 0.9;
      const offset = Math.abs(raw) > limit ? Math.sign(raw) * (limit + (Math.abs(raw) - limit) * 0.2) : raw;
      setState({
        offset,
        dragging: true,
        side: hasAction ? side : null,
        armed: hasAction && Math.abs(dx) >= commitAtRef.current(width),
      });
      if (!hasAction) dx = 0;
      if (endTimer) clearTimeout(endTimer);
      endTimer = setTimeout(end, WHEEL_END_MS);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (endTimer) clearTimeout(endTimer);
      if (coolTimer) clearTimeout(coolTimer);
    };
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => finish(e, false), [finish]);
  const onPointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => finish(e, true), [finish]);

  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (Date.now() < suppressUntil.current) {
      suppressUntil.current = 0;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    ref,
    state,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture },
  };
}
